import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { DASHBOARD_QUERY_KEY } from "../hooks/useDashboardData";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import {
  Box,
  Breadcrumbs,
  Paper,
  Typography,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link as MuiLink,
  CircularProgress,
  Collapse,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { formatViewCountSummary, normalizeViewCount } from "../utils/viewCount";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";

import { DeleteModal, NoticeboardButton } from "../components/shared";
import PageContainer from "../components/PageContainer";
import NoticeBodyHtml from "../components/notices/NoticeBodyHtml";
import NoticeCardPreview from "../components/CreationWizard/NoticeCardPreview";
import { parseSubcircleName } from "../utils/subcircleUtils";
import {
  getNoticeFeedTypeChipBackground,
  getNoticeFeedTypeChipLabel,
  resolveNoticeFeedTypeChip,
  type NoticeStoredType,
} from "../utils/noticeFeedChips";
import { useSubcircles } from "../hooks/useSubcircles";
import { useSnackbar } from "../context/SnackbarContext";
import { VideoProcessingAlert } from "../components/notices/VideoProcessingAlert";
import { VideoProcessingChip } from "../components/notices/VideoProcessingChip";
import { isVideoAttachmentProcessing } from "../utils/videoAttachment";
import { usePollNoticesWhileVideoProcessing } from "../hooks/usePollNoticesWhileVideoProcessing";
import PollResultsSection from "../components/notices/PollResultsSection";
import PollDetailsDashboard from "../components/notices/PollDetailsDashboard";
import { useUsers } from "../hooks/useUsers";
import SendReminderModal from "../components/ViewNotices/SendReminderModal";
import { noticeToShow, readNoticeHandover } from "../domain/notices/noticeHandover";
import NoticeDetailsSkeleton from "../components/notices/NoticeDetailsSkeleton";
import { buildManualReminderNoticePayload } from "../utils/noticeManualReminder";
import { buildNoticeDuplicatePayload } from "../utils/noticeDuplicate";
import {
  getNoticeEventBounds,
  isCalendarEventPast,
} from "../utils/noticeCalendarEvents";
import { isStaffOnlyNotice, canDashboardUserDeleteNotice } from "../utils/noticeScheduling";
import {
  getEventRecurrenceDisplay,
  resolveNoticeEventRecurrence,
} from "../utils/eventRecurrenceDisplay";
import type { PollResultsResponse } from "../domain/polls/types";
import type { EventRecurrence } from "../domain/events/eventRecurrence";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { useDeleteSentPlanner, usePlanner } from "../hooks/usePlanners";
import {
  isLiveEventIn,
  plannerDisplayTitle,
  plannerEventDeleteText,
} from "../domain/planners/planner";
import { VIEW_NOTICES, plannerHref } from "../routes";

// Utility function to convert UUID sender to a more readable format
const getReadableSenderName = (
  sender: string | undefined,
  user: any
): string => {
  if (!sender) return "Unknown";

  // If sender looks like a UUID, convert it to a more readable format
  if (
    sender.match(
      /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
    )
  ) {
    // Use the organization name - same logic as Profile page
    return user?.userType === "team"
      ? user.orgId
      : user.schoolName || "Organization";
  }

  return sender;
};

interface Attachment {
  name: string;
  url: string;
  type: string;
  size?: number;
}

interface Notice {
  noticeId: string;
  title: string;
  body: string;
  createdAt: string;
  type: "info" | "urgent" | "event" | "poll";
  pollOptions?: Array<{ optionId: string; label: string; order: number }>;
  pollClosesAt?: string;
  rsvpEnabled?: boolean;
  rsvpClosesAt?: string;
  targetSubcircle?: string;
  attachments?: Attachment[];
  sender: string;
  schoolName: string;
  viewCount?: {
    total: number;
    seen: number;
  };
  eventStartDate?: string;
  eventStartTime?: string;
  eventEndDate?: string;
  eventEndTime?: string;
  eventLocation?: string;
  eventBackgroundImage?: string;
  eventFontColor?: string;
  eventReminderMode?: string;
  eventReminderModes?: string[];
  targetUserId?: string;
  isUrgent?: boolean;
  eventSeriesId?: string;
  plannerId?: string;
  eventSeriesIndex?: number;
  eventSeriesTotal?: number;
  eventRecurrence?: EventRecurrence;
  isDeleted?: boolean;
  createdBy?: string;
  staffOnly?: boolean;
  publishAt?: string;
  publishStatus?: "draft" | "scheduled" | "published";
}

interface UserDetail {
  id: string;
  name: string;
  email: string;
}

interface NoticeDetailsProps {
  notice?: Notice;
  isPreview?: boolean;
}

const NoticeDetails: React.FC<NoticeDetailsProps> = ({
  notice: propNotice,
  isPreview,
}) => {
  const theme = useTheme();
  const [failedArtworkUrl, setFailedArtworkUrl] = useState<string>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const { subcircles } = useSubcircles();
  const { showMessage } = useSnackbar();
  const noticeFromState = propNotice || location.state?.notice;
  // Opened in a new tab (Nova's "Open original"), there's only the address.
  const { id: idFromAddress } = useParams<{ id: string }>();
  const isPollNotice = noticeFromState?.type === "poll";
  const pollTargetSubcircle = noticeFromState?.targetSubcircle;
  const isEntireCircleAudience =
    !pollTargetSubcircle || pollTargetSubcircle === user?.schoolId;

  const { data: orgUsers = [], isLoading: orgUsersLoading } = useUsers({
    enabled: isPollNotice && !isPreview,
  });

  const { data: subcircleAudience, isLoading: subcircleUsersLoading } =
    useQuery({
      queryKey: ["subcircleUsers", pollTargetSubcircle, user?.schoolId],
      queryFn: async () => {
        if (!pollTargetSubcircle || !user?.schoolId || !token) {
          throw new Error("Missing subcircle context");
        }
        return api.circles.getSubCircleUsers(
          pollTargetSubcircle,
          user.schoolId,
          token
        );
      },
      enabled:
        isPollNotice &&
        !isPreview &&
        !isEntireCircleAudience &&
        !!pollTargetSubcircle &&
        !!user?.schoolId &&
        !!token,
      staleTime: 5 * 60 * 1000,
    });

  // Fetch fresh notice data to check if it's deleted
  const { data: noticesData } = useQuery({
    queryKey: ["notices", user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId || !token) return null;
      return await api.notices.getAll(user.schoolId, token);
    },
    enabled:
      !!user?.schoolId &&
      !!token &&
      !!(noticeFromState?.noticeId || idFromAddress) &&
      !isPreview,
    staleTime: 0, // Always fetch fresh when checking notice details
  });

  // Use fresh notice from query if available, otherwise fall back to state
  const noticeShown = isPreview
    ? noticeFromState
    : noticeToShow(noticeFromState, idFromAddress, noticesData);
  const notice = noticeShown === "loading" ? undefined : noticeShown;

  const isInternalStaffNotice = notice ? isStaffOnlyNotice(notice) : false;

  const recurrenceDisplay = useMemo(() => {
    if (!notice) return null;
    const recurrence = resolveNoticeEventRecurrence(
      notice,
      noticesData ?? undefined,
    );
    return getEventRecurrenceDisplay(recurrence, notice);
  }, [notice, noticesData]);

  const [userDetails, setUserDetails] = useState<UserDetail[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState(false);
  const [viewData, setViewData] = useState<{
    total: number;
    seen: number;
  } | null>(null);
  const [pollResults, setPollResults] = useState<PollResultsResponse | null>(
    null
  );
  const [pollResultsLoading, setPollResultsLoading] = useState(false);
  const [pollResultsError, setPollResultsError] = useState<string | null>(null);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderTargetMember, setReminderTargetMember] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [sendingReminder, setSendingReminder] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // A Planner Event links back to its Planner, and can be deleted with all its
  // Planner's Events. Neither shows while Planner is turned off.
  const { isPlannerEnabled } = useFeatureFlags();
  const plannerId = isPlannerEnabled && !isPreview ? notice?.plannerId : undefined;
  const { data: planner } = usePlanner(plannerId);
  const plannerName = planner ? plannerDisplayTitle(planner.title) : undefined;
  const deleteSentPlanner = useDeleteSentPlanner();
  const [deleteWholePlanner, setDeleteWholePlanner] = useState(false);

  // Check if notice is deleted and redirect if so
  useEffect(() => {
    if (!isPreview && notice && notice.isDeleted) {
      navigate("/view-notices");
    }
  }, [notice, isPreview, navigate]);

  // Ask Noticeboard's "send a reminder" handoff: open Send reminder ready, nothing sent on its own
  useEffect(() => {
    if (isPreview || !notice || isInternalStaffNotice) return;
    const { openReminder } = readNoticeHandover(location.state);
    if (!openReminder) return;
    setReminderModalOpen(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: { notice } });
  }, [isPreview, notice, isInternalStaffNotice, location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (isPreview || !notice?.noticeId) return;

    const isEventNotice =
      notice.type === "event" || notice.type === "custom_event";
    if (!isEventNotice) return;

    const bounds = getNoticeEventBounds(notice);
    if (!bounds || !isCalendarEventPast(bounds.end)) return;

    showMessage({
      message: "This event has been completed.",
      severity: "info",
    });
  }, [isPreview, notice, showMessage]);

  // Fetch user details when notice is viewed
  useEffect(() => {
    if (isPreview) return; // Don't fetch user details in preview mode
    const fetchUserDetails = async () => {
      if (!notice?.noticeId || !user?.schoolId || !token) return;

      setLoadingUsers(true);
      try {
        const result = await api.notices.getReads(
          notice.noticeId,
          user.schoolId,
          token,
          { targetSubcircle: notice.targetSubcircle }
        );
        setUserDetails(result.users || []);
        setViewData({ total: result.total, seen: result.seen });
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUserDetails();
  }, [
    notice?.noticeId,
    notice?.targetSubcircle,
    user?.schoolId,
    token,
    isPreview,
  ]);

  const showChoiceResults =
    notice?.type === "poll" ||
    (notice?.type === "event" && notice?.rsvpEnabled === true);

  useEffect(() => {
    if (isPreview || !notice?.noticeId || !showChoiceResults || !token) {
      return;
    }
    const loadPollResults = async () => {
      setPollResultsLoading(true);
      setPollResultsError(null);
      try {
        const data = await api.notices.getPollResults(notice.noticeId, token);
        setPollResults(data);
      } catch (err) {
        console.error("Failed to load choice results:", err);
        setPollResultsError(
          notice.type === "poll"
            ? "Could not load poll results."
            : "Could not load RSVP summary."
        );
      } finally {
        setPollResultsLoading(false);
      }
    };
    loadPollResults();
  }, [
    notice?.noticeId,
    notice?.type,
    notice?.rsvpEnabled,
    showChoiceResults,
    token,
    isPreview,
  ]);

  const attachments = useMemo((): Attachment[] => {
    if (!notice?.attachments) return [];

    if (typeof notice.attachments === "string") {
      try {
        return JSON.parse(notice.attachments);
      } catch (e) {
        console.error("Failed to parse attachments:", e);
        return [];
      }
    }

    if (Array.isArray(notice.attachments)) {
      return notice.attachments;
    }

    return [];
  }, [notice]);

  // Must stay before `if (!notice) return` (rules-of-hooks).
  usePollNoticesWhileVideoProcessing(
    notice ? [{ attachments }] : undefined,
    !isPreview && !!notice
  );

  const audienceMembers = useMemo(() => {
    if (!isPollNotice) return [];
    if (!isEntireCircleAudience && subcircleAudience?.users) {
      return subcircleAudience.users.map((member) => ({
        id: member.userId,
        name: member.name,
        email: member.email,
      }));
    }
    return orgUsers.map((member) => ({
      id: member.id,
      name: member.name,
      email: member.email,
    }));
  }, [
    isPollNotice,
    isEntireCircleAudience,
    subcircleAudience,
    orgUsers,
  ]);

  const audienceLoading =
    isPollNotice &&
    (orgUsersLoading || (!isEntireCircleAudience && subcircleUsersLoading));

  if (noticeShown === "loading") return <NoticeDetailsSkeleton />;

  if (!notice) {
    return (
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          maxWidth: 800,
          mx: "auto",
          mt: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <NoticeboardButton
          variant="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/view-notices")}
          size="medium"
          sx={{ mb: 2 }}
        >
          Back to Notices
        </NoticeboardButton>
        <Typography color="text.secondary">Notice not found</Typography>
      </Box>
    );
  }

  const handleSendReminderConfirm = async () => {
    if (!notice?.noticeId || !token || isPreview) return;
    setSendingReminder(true);
    try {
      await api.notices.create(
        buildManualReminderNoticePayload(
          notice as import("../services/api/types").Notice,
          reminderTargetMember
            ? { targetUserId: reminderTargetMember.id }
            : undefined
        ) as Parameters<typeof api.notices.create>[0],
        token
      );
      setReminderModalOpen(false);
      setReminderTargetMember(null);
      showMessage({
        message: reminderTargetMember
          ? `Reminder sent to ${reminderTargetMember.name}.`
          : "Reminder sent. Parents will receive a new notice and push.",
        severity: "success",
      });
      await queryClient.refetchQueries({ queryKey: ["notices"] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
    } catch (err) {
      console.error("Failed to send reminder:", err);
      showMessage({
        message:
          err instanceof Error
            ? err.message
            : "Could not send reminder. Please try again.",
        severity: "error",
      });
    } finally {
      setSendingReminder(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith("image/")) return "🖼️";
    if (contentType === "application/pdf") return "📄";
    if (contentType.includes("word")) return "📝";
    if (contentType.includes("excel") || contentType.includes("spreadsheet"))
      return "📊";
    return "📎";
  };

  const canDeleteThisNotice =
    !isPreview &&
    !!notice &&
    !!token &&
    !!user?.schoolId &&
    canDashboardUserDeleteNotice(notice, user);

  // Deleting all is all or nothing, so it's offered only when every Event in the Planner can be deleted.
  const plannerEvents = plannerId && noticesData ? noticesData.filter(isLiveEventIn(plannerId)) : [];
  const canDeleteWholePlanner =
    canDeleteThisNotice &&
    plannerEvents.length > 0 &&
    plannerEvents.every((n) => canDashboardUserDeleteNotice(n, user));

  const handleDeleteClick = () => {
    if (isPreview) return;
    setDeleteWholePlanner(false);
    setDeleteDialogOpen(true);
  };

  const handleDeletePlannerConfirm = () => {
    if (!notice?.noticeId || !plannerId) return;
    deleteSentPlanner.mutate(
      { plannerId, anyEventId: notice.noticeId },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          navigate(VIEW_NOTICES);
        },
      },
    );
  };

  const handleDeleteConfirm = async () => {
    if (!notice?.noticeId || !token || !user?.schoolId) return;

    setIsDeleting(true);
    try {
      await api.notices.delete(notice.noticeId, user.schoolId, token);
      setDeleteDialogOpen(false);

      // Optimistically remove the notice from cache immediately
      queryClient.setQueryData(["notices", user.schoolId], (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter((n: any) => n.noticeId !== notice.noticeId);
      });

      // Invalidate and refetch notices to ensure consistency
      await queryClient.refetchQueries({ queryKey: ["notices"] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });

      navigate("/view-notices"); // Navigate back to notices list
    } catch (error) {
      console.error("Failed to delete notice:", error);
      // Revert optimistic update on error
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      // You might want to show an error message here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const typeChip = resolveNoticeFeedTypeChip(
    notice.type as NoticeStoredType,
    attachments
  );

  // Helper function to get circle/subcircle name
  const getCircleSubcircleName = (targetSubcircle?: string): string => {
    if (!targetSubcircle) {
      return user?.schoolName || "All Students";
    }

    // Check if it's the main organization circle (matches schoolId)
    if (targetSubcircle === user?.schoolId) {
      return user?.schoolName || "All Students";
    }

    // Try to find the subcircle in the subcircles list
    if (subcircles && subcircles.length > 0) {
      const subcircle = subcircles.find(
        (sc) => sc.circleId === targetSubcircle
      );
      if (subcircle && subcircle.name) {
        return subcircle.name;
      }
    }

    // Fallback to parsing the ID if we can't find it in the list
    return parseSubcircleName(targetSubcircle);
  };

  const artworkImage = notice.type === "image"
    ? notice.attachments?.find((file: { type?: string; url: string }) => file.type?.startsWith("image/") || /\.(jpe?g|png|webp|gif|avif)(?:[?#]|$)/i.test(file.url))
    : undefined;

  return (
    <PageContainer sx={{ mt: isPreview ? 0 : 2 }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!isPreview && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Breadcrumbs
              separator={<ChevronRightIcon sx={{ fontSize: 18, color: "text.secondary" }} />}
              aria-label="breadcrumb"
              sx={{ "& .MuiBreadcrumbs-li": { minWidth: 0 }, overflowWrap: "anywhere" }}
            >
              <MuiLink component={RouterLink} to="/" aria-label="Home" underline="hover" color="text.secondary" sx={{ display: "inline-flex", alignItems: "center" }}>
                <HomeOutlinedIcon sx={{ fontSize: 22 }} />
              </MuiLink>
              <MuiLink component={RouterLink} to="/view-notices" underline="hover" color="text.secondary">
                Notices
              </MuiLink>
              <Typography color="text.primary" aria-current="page" sx={{ fontWeight: 600 }}>
                {notice.title}
              </Typography>
            </Breadcrumbs>
          </Box>
        )}

        {isPollNotice && showChoiceResults ? (
          <PollDetailsDashboard
            title={notice.title}
            circleLabel={getCircleSubcircleName(notice.targetSubcircle)}
            createdAt={notice.createdAt}
            viewData={viewData}
            typeChip={typeChip}
            results={pollResults}
            loading={pollResultsLoading}
            error={pollResultsError}
            fallbackOptions={notice.pollOptions}
            audienceMembers={audienceMembers}
            audienceLoading={audienceLoading}
            viewerHints={userDetails.map((viewer) => ({
              id: viewer.id,
              name: viewer.name,
              email: viewer.email,
            }))}
            isPreview={isPreview}
            canDelete={canDeleteThisNotice}
            sendingReminder={sendingReminder}
            onSendReminder={
              isInternalStaffNotice
                ? undefined
                : (member) => {
                    setReminderTargetMember(
                      member ? { id: member.id, name: member.name } : null
                    );
                    setReminderModalOpen(true);
                  }
            }
            onDelete={handleDeleteClick}
          />
        ) : (
        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, md: 3 },
            width: "100%",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            mb: 2,
          }}
        >
          {/* Title Section */}
          <Typography
            variant="h5"
            color="text.primary"
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
              whiteSpace: "normal",
              mb: 2,
              fontSize: `calc(${theme.typography.h5.fontSize} + 2px)`,
              fontWeight: 600,
            }}
          >
            {notice.title}
          </Typography>

          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 400px) minmax(0, 1fr)" },
            gap: { xs: 3, md: 4 },
            alignItems: "start",
          }}>
            <Box component="section" aria-label="Notice artwork" sx={{ minWidth: 0 }}>
              {artworkImage && artworkImage.url !== failedArtworkUrl ? (
                <Box component="img"
                  src={artworkImage.url}
                  alt={notice.title}
                  onError={(event) => setFailedArtworkUrl(artworkImage.url)}
                  sx={{ display: "block", width: "100%", height: "auto", borderRadius: 2 }}
                />
              ) : <NoticeCardPreview
                layout="native"
                title={notice.title}
                body={notice.body || ""}
                isEvent={notice.type === "event" || notice.type === "custom_event"}
                eventStartDate={notice.eventStartDate ? new Date(notice.eventStartDate) : null}
                eventStartTime={notice.eventStartTime}
                eventLocation={notice.eventLocation}
                eventBackgroundImage={notice.eventBackgroundImage}
              />}
              {notice.body?.trim() ? (
                <Box sx={{ mt: 3, overflowWrap: "anywhere" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Message</Typography>
                  <NoticeBodyHtml body={notice.body} />
                </Box>
              ) : null}
            </Box>
            <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          {/* Event Information for Event Notices */}
          {notice.type === "event" &&
            (notice.eventStartDate || notice.eventLocation) && (
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 3,
                  p: { xs: 2.5, sm: 3 },
                  mb: 2,
                  color: "text.primary",
                }}
              >
                <Typography variant="h6" fontWeight={500} sx={{ mb: 2.5 }}>
                  Event details
                </Typography>
                <Stack spacing={2}>
                  {notice.eventStartDate && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTimeOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                      <Typography variant="body1">
                        {new Date(notice.eventStartDate).toLocaleDateString("en-GB", {
                          day: "numeric", month: "long",
                        })}
                        {notice.eventStartTime && `, ${notice.eventStartTime}`}
                      </Typography>
                    </Stack>
                  )}
                  {(notice.eventEndDate || notice.eventEndTime) && (
                    <Typography variant="body1">
                      Until{(notice.eventEndDate || notice.eventStartDate) && ` ${new Date(
                        notice.eventEndDate || notice.eventStartDate
                      ).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}`}
                      {notice.eventEndTime && `${notice.eventEndDate || notice.eventStartDate ? "," : ""} ${notice.eventEndTime}`}
                    </Typography>
                  )}
                  {notice.eventLocation && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MyLocationOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                      <Typography variant="body1">{notice.eventLocation}</Typography>
                    </Stack>
                  )}
                </Stack>
                {recurrenceDisplay ? (
                  <Stack spacing={1} sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "primary.200" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <RepeatOutlinedIcon
                        sx={{ fontSize: 18, color: "primary.main" }}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {recurrenceDisplay.frequencyLabel}
                        {recurrenceDisplay.endsLabel
                          ? ` until ${recurrenceDisplay.endsLabel}`
                          : ""}
                      </Typography>
                    </Stack>
                    {recurrenceDisplay.occurrenceLabel ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ pl: 3.25 }}
                      >
                        {recurrenceDisplay.occurrenceLabel}
                        {typeof notice.eventSeriesIndex === "number" &&
                        notice.eventSeriesIndex > 0
                          ? " · This page is for one date in the series"
                          : ""}
                      </Typography>
                    ) : null}
                  </Stack>
                ) : null}
                {plannerId ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "primary.200" }}
                  >
                    <EventNoteOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />
                    <Typography variant="body2" fontWeight={500}>
                      Part of{" "}
                      <MuiLink component={RouterLink} to={plannerHref(plannerId)}>
                        {plannerName ?? "a Planner"}
                      </MuiLink>
                    </Typography>
                  </Stack>
                ) : null}
              </Box>
            )}
          <Typography variant="subtitle1" fontWeight={400} sx={{ mt: 3 }}>
            Audience
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              mt: 2,
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Chip
              label={getNoticeFeedTypeChipLabel(typeChip)}
              size="small"
              sx={{
                bgcolor: getNoticeFeedTypeChipBackground(theme, typeChip),
                color: theme.palette.common.white,
                fontWeight: 600,
                fontSize: 12,
                height: 28,
                px: 2,
                borderRadius: 2,
              }}
            />
            <Chip
              label={getCircleSubcircleName(notice.targetSubcircle)}
              size="small"
              sx={{
                bgcolor: "background.paper",
                color: theme.palette.text.secondary,
                fontWeight: 600,
                fontSize: 12,
                height: 28,
                px: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" fontWeight={400} sx={{ mt: 3, mb: 1 }}>
            Delivery
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            mb={1}
            sx={{ color: "text.secondary", fontSize: "0.813rem", alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PersonIcon sx={{ fontSize: "1rem", opacity: 0.7 }} />
              <span>{getReadableSenderName(notice.sender, user)}</span>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CalendarTodayIcon sx={{ fontSize: "1rem", opacity: 0.7 }} />
              <span>{new Date(notice.createdAt).toLocaleString()}</span>
            </Stack>
            {viewData && (
              <span style={{ display: "flex", alignItems: "center" }}>
                Views: {formatViewCountSummary(viewData)}
              </span>
            )}
          </Stack>

          {showChoiceResults &&
            !isPreview &&
            notice.type === "event" &&
            notice.rsvpEnabled && (
            <PollResultsSection
              results={pollResults}
              loading={pollResultsLoading}
              error={pollResultsError}
              fallbackOptions={notice.pollOptions}
              choiceKind="rsvp"
              viewerHints={userDetails.map((u) => ({
                name: u.name,
                email: u.email,
              }))}
            />
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <VideoProcessingAlert attachments={attachments} />
              <Box sx={{ bgcolor: "background.paper", borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  Attachments ({attachments.length})
                </Typography>
                <List dense>
                  {attachments.map((att: Attachment, index: number) => {
                    const videoProcessing = isVideoAttachmentProcessing(att);
                    return (
                    <ListItem key={index} disableGutters>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "center", sm: "flex-start" },
                          width: "100%",
                        }}
                      >
                        <ListItemIcon
                          sx={{ minWidth: 32, justifyContent: "center" }}
                        >
                          {att.type && att.type.startsWith("image/") ? (
                            <a
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={att.url}
                                alt={att.name}
                                loading="lazy"
                                decoding="async"
                                style={{
                                  maxWidth: 120,
                                  maxHeight: 120,
                                  borderRadius: 8,
                                  border: `1px solid ${theme.palette.divider}`,
                                  marginBottom: 8,
                                  display: "block",
                                }}
                              />
                            </a>
                          ) : (
                            <Typography sx={{ fontSize: "1.25rem" }}>
                              {getFileIcon(att.type)}
                            </Typography>
                          )}
                        </ListItemIcon>
                        <Box
                          sx={{
                            textAlign: { xs: "center", sm: "left" },
                            ml: { xs: 0, sm: 2 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <MuiLink
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ fontWeight: 500, wordBreak: "break-all" }}
                            >
                              {att.name}
                            </MuiLink>
                            {videoProcessing && (
                              <VideoProcessingChip label="Processing video" />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: "text.secondary" }}
                          >
                            {att.size ? formatFileSize(att.size) : undefined}
                            {videoProcessing
                              ? " · Optimized playback pending"
                              : undefined}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                    );
                  })}
                </List>
              </Box>
            </>
          )}

          {!isPreview && !isInternalStaffNotice && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{
                mt: 3,
                pt: 1,
                mb: 2,
                width: "100%",
                flexWrap: "wrap",
                "& > *": {
                  width: { xs: "100%", sm: "auto" },
                },
              }}
            >
              <NoticeboardButton
                variant="secondary"
                size="medium"
                startIcon={<ContentCopyOutlinedIcon />}
                onClick={() => {
                  if (!notice) return;
                  navigate("/create-notice", {
                    state: {
                      prefillNotice: buildNoticeDuplicatePayload(notice),
                      returnTo: `${location.pathname}${location.search}`,
                    },
                  });
                }}
                sx={{
                  borderRadius: 1,
                  height: { xs: 40, sm: 32 },
                  py: { xs: 1, sm: 0.75 },
                  px: 2,
                }}
              >
                Duplicate
              </NoticeboardButton>
              <NoticeboardButton
                variant="secondary"
                size="medium"
                startIcon={<NotificationsActiveOutlinedIcon />}
                onClick={() => setReminderModalOpen(true)}
                loading={sendingReminder}
                sx={{
                  borderRadius: 1,
                  height: { xs: 40, sm: 32 },
                  py: { xs: 1, sm: 0.75 },
                  px: 2,
                }}
              >
                Send reminder
              </NoticeboardButton>
              {canDeleteThisNotice && (
                <NoticeboardButton
                  variant="danger"
                  size="medium"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                  sx={{
                    borderRadius: 1,
                    height: { xs: 40, sm: 32 },
                    py: { xs: 1, sm: 0.75 },
                    px: 2,
                  }}
                >
                  Remove
                </NoticeboardButton>
              )}
            </Stack>
          )}

          {/* Users who have seen this notice */}
          {loadingUsers ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            userDetails.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{ bgcolor: "background.paper", borderRadius: 1, p: 2 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: "pointer" }}
                    onClick={() => setExpandedUsers(!expandedUsers)}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Viewers ({userDetails.length} of{" "}
                      {normalizeViewCount(viewData).total})
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedUsers(!expandedUsers);
                      }}
                      sx={{
                        transform: expandedUsers
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  </Stack>
                  <Collapse in={expandedUsers}>
                    <List dense sx={{ mt: 1 }}>
                      {userDetails.map((user: UserDetail, index: number) => (
                        <ListItem key={user.id || index} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <PersonIcon
                              sx={{ fontSize: 20, color: "primary.main" }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={user.name}
                            secondary={user.email}
                            primaryTypographyProps={{
                              variant: "body2",
                              fontWeight: 500,
                            }}
                            secondaryTypographyProps={{
                              variant: "caption",
                              color: "text.secondary",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              </>
            )
          )}

            </Box>
          </Box>
        </Paper>
        )}
      </Box>

      <SendReminderModal
        open={reminderModalOpen}
        onClose={() => {
          if (sendingReminder) return;
          setReminderModalOpen(false);
          setReminderTargetMember(null);
        }}
        noticeTitle={notice.title}
        recipientName={reminderTargetMember?.name}
        onConfirm={handleSendReminderConfirm}
        loading={sendingReminder}
      />

      <DeleteModal
        open={deleteDialogOpen}
        onClose={() => {
          if (isDeleting || deleteSentPlanner.isPending) return;
          handleDeleteCancel();
        }}
        title={canDeleteWholePlanner ? "Delete this Event?" : "Remove Notice"}
        subtitle={canDeleteWholePlanner ? "This can't be undone." : "This action cannot be undone"}
        maxWidth="sm"
      >
        {canDeleteWholePlanner ? (
          <RadioGroup
            aria-label="What to delete"
            value={deleteWholePlanner ? "planner" : "event"}
            onChange={(event) => setDeleteWholePlanner(event.target.value === "planner")}
            sx={{ mb: 3 }}
          >
            <FormControlLabel
              value="event"
              control={<Radio />}
              label={
                <>
                  <Typography fontWeight={500}>Just this Event</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {plannerEventDeleteText(notice.title, plannerEvents.length)}
                  </Typography>
                </>
              }
              sx={{ alignItems: "flex-start", mb: 1.5, "& .MuiRadio-root": { pt: 0.25 } }}
            />
            <FormControlLabel
              value="planner"
              control={<Radio />}
              label={
                <>
                  <Typography fontWeight={500}>
                    All Events in {plannerName ?? "this Planner"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Every Event in it comes off parents&apos; Calendars and their Event
                    reminders stop. The Planner is deleted too.
                  </Typography>
                </>
              }
              sx={{ alignItems: "flex-start", "& .MuiRadio-root": { pt: 0.25 } }}
            />
          </RadioGroup>
        ) : (
          <Typography
            variant="body1"
            sx={{ mb: 3, fontSize: "1rem", lineHeight: 1.6 }}
          >
            Are you sure you want to remove the notice{" "}
            <Typography component="span" fontWeight={600}>
              &ldquo;{notice.title}&rdquo;
            </Typography>
            ? This action cannot be undone and the notice will be permanently
            removed.
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          {deleteWholePlanner && canDeleteWholePlanner ? (
            <NoticeboardButton
              variant="danger"
              onClick={handleDeletePlannerConfirm}
              loading={deleteSentPlanner.isPending}
            >
              Delete all Events
            </NoticeboardButton>
          ) : (
            <NoticeboardButton
              variant="danger"
              onClick={handleDeleteConfirm}
              loading={isDeleting}
              disabled={isDeleting}
            >
              {isDeleting ? "Removing..." : canDeleteWholePlanner ? "Delete this Event" : "Remove Notice"}
            </NoticeboardButton>
          )}
        </Box>
      </DeleteModal>
    </PageContainer>
  );
};

export default NoticeDetails;
